import { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingBag, ArrowDownCircle, ArrowUpCircle, Search } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MEDIDAS = ['90x130', '70x110'];

const initialForm = {
    tipo: 'entrada', medida: '90x130', cantidad: '', proveedor_id: '', observaciones: ''
};

export const Bolsas = () => {
    const [movimientos, setMovimientos] = useState([]);
    const [inventario, setInventario] = useState([]);
    const [proveedores, setProveedores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(initialForm);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [movRes, invRes, provRes] = await Promise.all([
                axios.get(`${API_URL}/api/bolsas`),
                axios.get(`${API_URL}/api/bolsas/inventario`),
                axios.get(`${API_URL}/api/proveedores`)
            ]);
            setMovimientos(movRes.data);
            setInventario(invRes.data);
            setProveedores(provRes.data);
        } catch (e) { toast.error('Error al cargar datos'); }
        finally { setLoading(false); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.medida || !form.cantidad) {
            toast.error('Complete todos los campos requeridos'); return;
        }
        try {
            await axios.post(`${API_URL}/api/bolsas`, { ...form, cantidad: parseInt(form.cantidad) });
            toast.success(`${form.tipo === 'entrada' ? 'Entrada' : 'Salida'} registrada`);
            setModalOpen(false);
            setForm(initialForm);
            fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al registrar'); }
    };

    const openModal = (tipo) => { setForm({ ...initialForm, tipo }); setModalOpen(true); };

    const filtered = movimientos.filter(m =>
        (m.medida || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (m.observaciones || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="bolsas-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Inventario de Bolsas</h1>
                    <p className="text-zinc-400 mt-1">Control de bolsas plasticas (90x130 y 70x110)</p>
                </div>
                <div className="flex gap-2">
                    <Button onClick={() => openModal('entrada')} className="bg-emerald-500 hover:bg-emerald-600" data-testid="btn-entrada-bolsa">
                        <ArrowDownCircle className="w-5 h-5 mr-2" /> Entrada
                    </Button>
                    <Button onClick={() => openModal('salida')} className="bg-red-500 hover:bg-red-600" data-testid="btn-salida-bolsa">
                        <ArrowUpCircle className="w-5 h-5 mr-2" /> Salida
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                {MEDIDAS.map((medida) => {
                    const item = inventario.find(i => i.medida === medida) || { medida, stock: 0 };
                    return (
                        <Card key={medida} className="card">
                            <CardContent className="p-5">
                                <div className="flex items-center gap-3 mb-2">
                                    <ShoppingBag className="w-6 h-6 text-amber-400" />
                                    <p className="text-lg font-semibold text-zinc-200">Bolsa {medida}</p>
                                </div>
                                <p className="text-3xl font-bold text-amber-400" data-testid={`stock-bolsa-${medida}`}>{item.stock}</p>
                                <p className="text-xs text-zinc-500 mt-1">unidades en stock</p>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
            </div>

            <Card className="card"><CardContent className="p-0"><div className="overflow-x-auto">
                <Table><TableHeader><TableRow className="bg-zinc-800">
                    <TableHead className="table-header">Fecha</TableHead>
                    <TableHead className="table-header">Tipo Mov.</TableHead>
                    <TableHead className="table-header">Medida</TableHead>
                    <TableHead className="table-header">Cantidad</TableHead>
                    <TableHead className="table-header">Observaciones</TableHead>
                    <TableHead className="table-header">Usuario</TableHead>
                </TableRow></TableHeader><TableBody>
                    {filtered.length > 0 ? filtered.map(m => (
                        <TableRow key={m.id} className="table-row">
                            <TableCell className="table-cell">{m.created_at?.split('T')[0]}</TableCell>
                            <TableCell className="table-cell">
                                <span className={m.tipo === 'entrada' ? 'badge-success' : 'badge-danger'}>{m.tipo === 'entrada' ? 'Entrada' : 'Salida'}</span>
                            </TableCell>
                            <TableCell className="table-cell font-medium">{m.medida}</TableCell>
                            <TableCell className="table-cell">
                                <span className={m.tipo === 'entrada' ? 'text-emerald-400' : 'text-red-400'}>{m.tipo === 'entrada' ? '+' : '-'}{m.cantidad}</span>
                            </TableCell>
                            <TableCell className="table-cell text-zinc-400">{m.observaciones || '-'}</TableCell>
                            <TableCell className="table-cell">{m.usuario_nombre || '-'}</TableCell>
                        </TableRow>
                    )) : (
                        <TableRow><TableCell colSpan={6} className="text-center py-8 text-zinc-400">Sin movimientos</TableCell></TableRow>
                    )}
                </TableBody></Table>
            </div></CardContent></Card>

            <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                <DialogContent className="sm:max-w-[500px]" data-testid="modal-bolsa">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            {form.tipo === 'entrada' ? <ArrowDownCircle className="w-5 h-5 text-emerald-400" /> : <ArrowUpCircle className="w-5 h-5 text-red-400" />}
                            {form.tipo === 'entrada' ? 'Entrada' : 'Salida'} de Bolsas
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group"><Label className="form-label">Medida *</Label>
                                <Select value={form.medida} onValueChange={v => setForm({ ...form, medida: v })}>
                                    <SelectTrigger data-testid="select-medida-bolsa"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {MEDIDAS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="form-group"><Label className="form-label">Cantidad *</Label>
                                <Input type="number" min="1" value={form.cantidad} onChange={e => setForm({ ...form, cantidad: e.target.value })} data-testid="input-cantidad-bolsa" />
                            </div>
                        </div>
                        {form.tipo === 'entrada' && (
                            <div className="form-group"><Label className="form-label">Proveedor</Label>
                                <Select value={form.proveedor_id} onValueChange={v => setForm({ ...form, proveedor_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                                    <SelectContent>{proveedores.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                        )}
                        <div className="form-group"><Label className="form-label">Observaciones</Label>
                            <Input value={form.observaciones} onChange={e => setForm({ ...form, observaciones: e.target.value })} placeholder="Notas" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
                            <Button type="submit" className={form.tipo === 'entrada' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'} data-testid="submit-bolsa">
                                Registrar {form.tipo === 'entrada' ? 'Entrada' : 'Salida'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
